import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AdminRoute, FullScreenLoader, ProtectedRoute } from "@/components/auth/RouteGuards";
import { AppShell } from "@/components/layout/AppShell";
import { AuthProvider } from "@/context/AuthContext";

const pages = {
  AuthCallback: () => import("@/pages/AuthCallback"),
  CalendarPage: () => import("@/pages/CalendarPage"),
  ChatPage: () => import("@/pages/ChatPage"),
  ClientsPage: () => import("@/pages/ClientsPage"),
  OperationalTablePage: () => import("@/pages/OperationalTablePage"),
  PaymentsPage: () => import("@/pages/PaymentsPage"),
  DrivePage: () => import("@/pages/DrivePage"),
  EditorialPlanPage: () => import("@/pages/EditorialPlanPage"),
  LoginPage: () => import("@/pages/LoginPage"),
  UsersPage: () => import("@/pages/UsersPage"),
  AgentsPage: () => import("@/pages/AgentsPage"),
};
const AuthCallback = lazy(pages.AuthCallback);
const CalendarPage = lazy(pages.CalendarPage);
const ChatPage = lazy(pages.ChatPage);
const ClientsPage = lazy(pages.ClientsPage);
const OperationalTablePage = lazy(pages.OperationalTablePage);
const PaymentsPage = lazy(pages.PaymentsPage);
const DrivePage = lazy(pages.DrivePage);
const EditorialPlanPage = lazy(pages.EditorialPlanPage);
const LoginPage = lazy(pages.LoginPage);
const UsersPage = lazy(pages.UsersPage);
const AgentsPage = lazy(pages.AgentsPage);

// Load the other sections quietly once the first one is on screen
const usePrefetchPages = () => {
  useEffect(() => {
    const run = () => Object.values(pages).forEach((load) => load().catch(() => {}));
    const id = window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 1500);
    return () => { (window.cancelIdleCallback || clearTimeout)(id); };
  }, []);
};

function AppRouter() {
  const location = useLocation();
  usePrefetchPages();
  if (location.hash?.includes("session_id=")) {
    return <Suspense fallback={<FullScreenLoader />}><AuthCallback /></Suspense>;
  }
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
          <Route path="/" element={<OperationalTablePage />} />
          <Route path="/utenti" element={<AdminRoute><UsersPage /></AdminRoute>} />
          <Route path="/tabella" element={<Navigate to="/" replace />} />
          <Route path="/clienti" element={<ClientsPage />} />
          <Route path="/piano-editoriale" element={<EditorialPlanPage />} />
          <Route path="/drive" element={<DrivePage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/calendario" element={<CalendarPage />} />
          <Route path="/pagamenti" element={<AdminRoute><PaymentsPage /></AdminRoute>} />
          <Route path="/agenti" element={<AdminRoute><AgentsPage /></AdminRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="usocial-theme">
      <TooltipProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
        <Toaster position="top-right" richColors closeButton />
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
